
import {
  BackupScheduleDto, ConfigResetListDto, ConfigResetRepositoryInterface,
  RestoreTaskId, RestoreTaskRepositoryInterface, RestoreTaskSummaryDto,
  TaskDto,
  TaskProgressStatus,
  TaskRepositoryInterface,
} from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import DeleteBackupScheduleDialog from '@/components/dialogs/backups/DeleteBackupScheduleDialog.vue';
import RestoreTaskDetailsDialog from "@/components/dialogs/backups/RestoreTaskDetailsDialog.vue";

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    RestoreTaskDetailsDialog,
    DeleteBackupScheduleDialog,
  },
  inject: [
    'taskRepository',
    'restoreTaskRepository',
    'configResetRepository'
  ],
})
export default class AllTaskSchedules extends Vue {
  get serverSearch(): string {
    return this._serverSearch ?? "";
  }

  set serverSearch(value: string) {
    this._serverSearch = value;
  }

  get type(): string {
    return this._type ?? "";
  }

  set type(value: string) {
    this._type = value;
  }

  public loading: boolean = false;
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalItems: number = 0;

  public taskRepository: TaskRepositoryInterface;
  public restoreTaskRepository: RestoreTaskRepositoryInterface;
  public configResetRepository: ConfigResetRepositoryInterface;

  public deleteDialog: string = '';
  public deleteDialogData: BackupScheduleDto | null = null;

  public tasks: TaskDto[] = [];

  private _type?: string;
  private _serverSearch?: string;

  public detailsDialog: boolean = false;

  public restoreTask: RestoreTaskSummaryDto;
  public configRestoreData: ConfigResetListDto | null = null;

  public types = [
    'Backup',
    'Forget',
    'Prune',
    "Restore",
  ];

  public statusLabels: { [key: number]: string } = {
    0: 'Pending',
    1: 'Running',
    2: 'Finished',
    3: 'Failed',
  };

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Target Server', value: 'targetServerId' },
    { text: 'Status', value: 'status' },
    { text: 'Created At', value: 'createdAt' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public searchOptions = {};

  public statusLabel(status: number): string {
    return this.statusLabels[status] || 'Unknown';
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getTasks();
    } catch (err) {
      this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getTasks(): Promise<void> {
    const result = await this.taskRepository.searchTasks(
      this._type?.length ? this._type : undefined,
      this._serverSearch?.length ? this._serverSearch : undefined,
      {
      page: this.currentPage,
      pageSize: this.itemsPerPage,
    });

    this.totalItems = result?.total ?? 0;
    this.tasks = (result?.data ?? []).map(x => ({
      ...x,
      idComposite: x.type + "_" + x.id,
    }));
  }

  public async changePage(): Promise<void> {
    await this.search();
  }

  public async showDetailsDialogFor(restoreId: RestoreTaskId): Promise<void> {
    if (this.restoreTask && this.restoreTask.id === restoreId && this.restoreTask.status > TaskProgressStatus.Running) return;

    this.restoreTask = await this.restoreTaskRepository.getRestoreTask(restoreId);
    await this.fetchRestoreTask(this.restoreTask.id, Object.keys(JSON.parse(this.restoreTask.websiteRestores[0].restoreConfigsJson)).length > 0);

    if (!this.restoreTask ) {
      return;
    }
    this.detailsDialog = true;
    // this.detailsDialog = 'restore-task-details-dialog';
  }

  public async showFailTaskDialogFor(type: string, taskId: number): Promise<void> {
    this.$infoDialog.show("Are you sure you want to switch the task to failed state?", () => {
      try {
        this.taskRepository.failTask(type === "Terminated Backup" ? "Backup" : type, taskId)
          .then(() => this.search());
      } catch (err) {
        this.onError(err);
      }
    });
  }

  public async openBackup(id: number): Promise<void> {
    await this.$router.push({ name: 'backup_details', params: { "backupId": String(id) } });
  }

  public async createTask(): Promise<void> {
    await this.$router.push({ name: 'task_create' });
  }

  public async deleteBackupSchedule(backupSchedule: BackupScheduleDto): Promise<void> {
    try {
      //await this.backupScheduleRepository.deleteBackupSchedule(backupSchedule.id);

      this.$successDialog.show('Backup schedule deleted successfully');

      await this.search();
    } catch (err) {
      this.onError(err);
    }
  }

  private async fetchRestoreTask(restoreId: RestoreTaskId, hasConfigs: boolean): Promise<void> {
    try {
      if (this.restoreTask) {
        if (hasConfigs) {
          const configReset = await this.configResetRepository.getConfigResetByTask(restoreId);
          if (configReset) {
            this.configRestoreData = configReset;
          }
        }
      }
      await this.fetchAdditionalData(restoreId, hasConfigs);
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  private async fetchAdditionalData(restoreId: RestoreTaskId, hasConfigs: boolean): Promise<void> {
    if (this.restoreTask) {
      if (hasConfigs) {
        const configReset = await this.configResetRepository.getConfigResetByTask(restoreId);
        if (configReset) {
          this.configRestoreData = configReset;
        }
      }
    }
  }

  private onError(err: unknown): void {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
